import { _ as _ts_decorate } from "@swc/helpers/_/_ts_decorate";
import { _ as _ts_metadata } from "@swc/helpers/_/_ts_metadata";
import { jsx as _jsx } from "react/jsx-runtime";
import { action, computed, flow, makeObservable, observable } from "mobx";
import { FolderNode } from "../models/folderNode.js";
import { UploadItem } from "../models/uploadItem.js";
import { humanFileSize, secondsFormat } from "../utils/fileSize.js";
import { __ } from "../utils/i18n.js";
import { request } from "../utils/request.js";
import { locationRestFoldersContentCountsGet } from "../wp-api/folders-content-counts.get.js";
import { locationRestFoldersPost } from "../wp-api/folders.post.js";
import { locationRestHierarchyPut } from "../wp-api/hierarchy.put.js";
import { locationRestTreeGet } from "../wp-api/tree.get.js";
class TreeStore {
    static ID_ALL = "all";
    rootId;
    staticTree = [];
    tree = [];
    slugs = {
        names: [],
        slugs: [],
        types: []
    };
    selected;
    foldersNeedsRefresh = [];
    uploading = [];
    uploadTotalLoaded = 0;
    uploadTotalSize = 0;
    uploadTotalBytesPerSec = 0;
    sortables;
    treeSortables;
    refs = new Map();
    rootStore;
    currentlySettingTree = 0;
    selectedChangeListeners = new Set();
    get selectedId() {
        return this.selected?.id;
    }
    get breadcrumb() {
        if (this.selected) {
            return this.selected.path.map((node)=>node.title);
        }
        return [
            /*#__PURE__*/ _jsx("i", {
                children: __("No folder selected")
            }, "0")
        ];
    }
    get currentUpload() {
        return this.uploading.length ? this.uploading[0] : undefined;
    }
    get uploadTotalRemainTime() {
        if (this.uploadTotalBytesPerSec > 0) {
            const remainTime = Math.floor((this.uploadTotalSize - this.uploadTotalLoaded) / this.uploadTotalBytesPerSec);
            return secondsFormat(remainTime);
        }
        return "00:00:00";
    }
    get readableUploadTotalLoaded() {
        return humanFileSize(this.uploadTotalLoaded);
    }
    get readableUploadTotalSize() {
        return humanFileSize(this.uploadTotalSize);
    }
    get readableUploadTotalBytesPerSec() {
        return humanFileSize(this.uploadTotalBytesPerSec);
    }
    constructor(rootStore, options){
        this.rootStore = rootStore;
        const { others } = rootStore.optionStore;
        this.rootId = +others.rootId;
        this.sortables = others.sortables.content;
        this.treeSortables = others.sortables.tree;
        makeObservable(this);
        if (!options?.skipInit) {
            this.initStaticTree();
        }
    }
    initStaticTree() {
        const { others } = this.rootStore.optionStore;
        this.staticTree = [
            new FolderNode({
                id: -2,
                title: "none",
                $visible: false,
                properties: {
                    type: -2
                }
            }, this),
            new FolderNode({
                id: TreeStore.ID_ALL,
                title: __("All files"),
                icon: "copy",
                count: others.allPostCnt ?? 0
            }, this),
            new FolderNode({
                id: +others.rootId,
                title: __("Unorganized"),
                icon: "home",
                count: 0,
                contentCustomOrder: 2,
                properties: {
                    type: 4
                }
            }, this)
        ];
    }
    subscribeSelectedChange(listener) {
        this.selectedChangeListeners.add(listener);
        return ()=>this.selectedChangeListeners.delete(listener);
    }
    setSelected(node) {
        if (this.selected) {
            this.selected.selected = false;
        }
        this.selected = node;
        if (node) {
            node.selected = true;
        }
        this.selectedChangeListeners.forEach((listener)=>listener());
    }
    normalizeTreeId(id) {
        if (id === false || id === undefined || id === null || id === "") {
            return false;
        }
        if (id === TreeStore.ID_ALL) {
            return id;
        }
        if (typeof id === "string" && /^-?\d+$/.test(id)) {
            return +id;
        }
        return id;
    }
    getTreeItemById(id, excludeStatic = true) {
        const lookupId = this.normalizeTreeId(id === undefined ? this.selectedId ?? false : id);
        if (lookupId === false) {
            return undefined;
        }
        let result = this.refs.get(lookupId);
        if (!result && typeof lookupId === "number") {
            result = this.refs.get(String(lookupId));
        }
        if (!result) {
            return undefined;
        }
        if (excludeStatic && this.staticTree.indexOf(result) > -1) {
            return undefined;
        }
        return result;
    }
    nodes(fn, isStatic = false) {
        const walk = (nodes)=>nodes.forEach((n)=>{
                fn(n);
                if (n.childNodes?.length) {
                    walk(n.childNodes);
                }
            });
        walk(isStatic ? this.staticTree : this.tree);
    }
    setTree(tree, isStatic = false, slugs = null) {
        this.currentlySettingTree++;
        if (isStatic) {
            this.staticTree = tree;
        } else {
            this.tree = tree;
        }
        if (slugs) {
            this.slugs = slugs;
        }
        this.currentlySettingTree--;
    }
    onChildAdded(parent) {
        if (this.currentlySettingTree === 0 && parent.subOrderAutomatically) {
            parent.applyChildrenOrder();
        }
    }
    handleSort = flow(function*(data) {
        const { id, oldIndex, newIndex, parentFromId, parentToId, nextId, request: doRequest = true } = data;
        const requestBody = {
            nextId: nextId === 0 ? false : nextId
        };
        let treeItem;
        if (parentFromId === this.rootId) {
            treeItem = this.tree[oldIndex];
            this.tree.splice(oldIndex, 1);
        } else {
            const parent = this.getTreeItemById(parentFromId);
            treeItem = parent.childNodes[oldIndex];
            parent.childNodes.splice(oldIndex, 1);
        }
        if (process.env.PLUGIN_CTX === "pro") {
        /* Silence is golden... Until you have PRO Version! */ }
        if (parentToId === this.rootId) {
            this.tree.splice(newIndex, 0, treeItem);
        } else {
            this.getTreeItemById(parentToId).childNodes.splice(newIndex, 0, treeItem);
        }
        if (!doRequest) {
            return true;
        }
        try {
            yield request({
                location: locationRestHierarchyPut,
                params: {
                    id
                },
                request: requestBody
            });
            return true;
        } catch (e) {
            yield this.handleSort({
                id,
                oldIndex: newIndex,
                newIndex: oldIndex,
                parentFromId: parentToId,
                parentToId: parentFromId,
                nextId,
                request: false
            });
            throw e;
        }
    });
    fetchTree = flow(function*(setSelectedId) {
        const result = yield request({
            location: locationRestTreeGet
        });
        const { tree, cntRoot, cntAll, slugs } = result;
        const mapped = tree.map((item)=>FolderNode.mapFromRestEndpoint.call(this, item));
        this.setTree(mapped, false, slugs);
        if (typeof setSelectedId !== "undefined") {
            const node = this.getTreeItemById(setSelectedId, false);
            if (node) {
                this.setSelected(node);
            }
        }
        const all = this.getTreeItemById(TreeStore.ID_ALL, false);
        if (all) {
            all.count = cntAll;
        }
        this.getTreeItemById(this.rootId, false).count = cntRoot;
        return result;
    });
    fetchCounts = flow(function*(counts) {
        if (counts) {
            Object.keys(counts).forEach((k)=>{
                const ref = this.getTreeItemById(k, false);
                if (ref) {
                    ref.count = counts[k];
                }
            });
            return counts;
        }
        return yield this.fetchCounts((yield request({
            location: locationRestFoldersContentCountsGet
        })));
    });
    persist = flow(function*(name, { parent, typeInt }, beforeAttach) {
        const response = yield request({
            location: locationRestFoldersPost,
            request: {
                name,
                parent,
                type: typeInt
            }
        });
        const newObj = FolderNode.mapFromRestEndpoint.call(this, response);
        beforeAttach?.();
        if (parent === this.rootId) {
            this.tree.push(newObj);
            this.onChildAdded(newObj);
        } else if (process.env.PLUGIN_CTX === "pro") {
        /* Silence is golden... Until you have PRO Version! */ }
        return newObj;
    });
    setUploadTotal({ loaded, size, bytesPerSec }) {
        this.uploadTotalLoaded = loaded;
        this.uploadTotalSize = size;
        this.uploadTotalBytesPerSec = bytesPerSec;
    }
    addUploading(upload) {
        const node = this.getTreeItemById(upload.node.id, false) ?? upload.node;
        if (!this.getTreeItemById(node.id, false)) {
            this.tree.push(node);
            this.addFoldersNeedsRefresh(node.id);
        }
        const item = new UploadItem({
            ...upload,
            node
        });
        this.uploading.push(item);
        return item;
    }
    addFoldersNeedsRefresh(id) {
        if (this.foldersNeedsRefresh.indexOf(id) === -1) {
            this.foldersNeedsRefresh.push(id);
        }
    }
    removeFoldersNeedsRefresh(id) {
        const idx = this.foldersNeedsRefresh.indexOf(id);
        if (idx > -1) {
            this.foldersNeedsRefresh.splice(idx, 1);
        }
    }
    removeUploading(cid) {
        for(let i = 0; i < this.uploading.length; i++){
            if (this.uploading[i].cid === cid) {
                const copy = this.uploading[i].toJSON();
                this.uploading.splice(i, 1);
                return copy;
            }
        }
        return undefined;
    }
}
_ts_decorate([
    observable,
    _ts_metadata("design:type", Number)
], TreeStore.prototype, "rootId", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Array)
], TreeStore.prototype, "staticTree", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Array)
], TreeStore.prototype, "tree", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], TreeStore.prototype, "slugs", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], TreeStore.prototype, "selected", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Array)
], TreeStore.prototype, "foldersNeedsRefresh", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Array)
], TreeStore.prototype, "uploading", void 0);
_ts_decorate([
    observable
], TreeStore.prototype, "uploadTotalLoaded", void 0);
_ts_decorate([
    observable
], TreeStore.prototype, "uploadTotalSize", void 0);
_ts_decorate([
    observable
], TreeStore.prototype, "uploadTotalBytesPerSec", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", typeof Record === "undefined" ? Object : Record)
], TreeStore.prototype, "sortables", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", typeof Record === "undefined" ? Object : Record)
], TreeStore.prototype, "treeSortables", void 0);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], TreeStore.prototype, "selectedId", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], TreeStore.prototype, "breadcrumb", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], TreeStore.prototype, "currentUpload", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], TreeStore.prototype, "uploadTotalRemainTime", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], TreeStore.prototype, "readableUploadTotalLoaded", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], TreeStore.prototype, "readableUploadTotalSize", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], TreeStore.prototype, "readableUploadTotalBytesPerSec", null);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", []),
    _ts_metadata("design:returntype", void 0)
], TreeStore.prototype, "initStaticTree", null);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        typeof FolderNode === "undefined" ? Object : FolderNode
    ]),
    _ts_metadata("design:returntype", void 0)
], TreeStore.prototype, "setSelected", null);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        Array,
        void 0,
        Object
    ]),
    _ts_metadata("design:returntype", void 0)
], TreeStore.prototype, "setTree", null);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        typeof FolderNode === "undefined" ? Object : FolderNode
    ]),
    _ts_metadata("design:returntype", void 0)
], TreeStore.prototype, "onChildAdded", null);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        Object
    ]),
    _ts_metadata("design:returntype", void 0)
], TreeStore.prototype, "setUploadTotal", null);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        Object
    ]),
    _ts_metadata("design:returntype", void 0)
], TreeStore.prototype, "addUploading", null);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        Object
    ]),
    _ts_metadata("design:returntype", void 0)
], TreeStore.prototype, "addFoldersNeedsRefresh", null);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        Object
    ]),
    _ts_metadata("design:returntype", void 0)
], TreeStore.prototype, "removeFoldersNeedsRefresh", null);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        String
    ]),
    _ts_metadata("design:returntype", void 0)
], TreeStore.prototype, "removeUploading", null);
export { TreeStore };
export { createUnorganizedNode, createAuxiliaryTreeStore, cloneNodesFlat, cloneTreeNodes, createAllNode } from "./treeStoreClone.js";
